import {
  type DataDrivenPropertyValueSpecification,
  GeolocateControl,
  Map as MapLibreMap,
  NavigationControl,
  ScaleControl,
  TerrainControl,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import type { Theme } from "./theme";
import { loadSavedViewport, persistViewportOnChange } from "./viewportPersistence";

// Public, free, no-API-key elevation tiles descended from the original
// Mapzen/AWS "Terrarium" DEM tileset. See:
// https://registry.opendata.aws/terrain-tiles/
const TERRAIN_DEM_URL = "https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png";

// OpenFreeMap's free, no-API-key "dark" style. We tried CARTO's Dark
// Matter style here briefly for its better default water/land contrast,
// but its tile/sprite/glyph CDN (tiles.basemaps.cartocdn.com) turned out
// to hang indefinitely on at least one deployment's network -- MapLibre's
// "load" event never fired, and nothing in the app ever rendered. That's
// not something we can fix in code; OpenFreeMap is the CDN we've actually
// confirmed loads, so it's the safer default. Its low contrast at world
// zoom (background rgb(12,12,12) vs water rgb(27,27,29)) is fixed below
// in improveLowZoomContrast() instead. Override via MAP_STYLE_URL if your
// network reaches CARTO fine and you'd rather have it out of the box.
const DEFAULT_NIGHT_STYLE_URL = "https://tiles.openfreemap.org/styles/dark";

// OpenFreeMap's "positron" style -- the light-mode counterpart, same CDN
// reliability reasoning as above. Override via MAP_STYLE_URL_DAY.
const DEFAULT_DAY_STYLE_URL = "https://tiles.openfreemap.org/styles/positron";

function styleUrlFor(theme: Theme): string {
  return theme === "day"
    ? (import.meta.env.VITE_MAP_STYLE_URL_DAY ?? DEFAULT_DAY_STYLE_URL)
    : (import.meta.env.VITE_MAP_STYLE_URL ?? DEFAULT_NIGHT_STYLE_URL);
}

/** Candidate vector source ids to try for 3D building extrusion, in
 * priority order. OpenFreeMap/OpenMapTiles-based styles publish an
 * "openmaptiles" source with a "building" layer carrying
 * `render_height`/`render_min_height` -- the de facto standard schema
 * most free OSM vector tile providers follow; CARTO's styles publish
 * their vector source as "carto" instead, with a "building" source-layer
 * but no per-feature height metadata (buildings extrude to the flat
 * default height below). If none of these match, we fall back to the
 * first vector source in the style so a custom MAP_STYLE_URL still gets
 * *some* attempt at buildings. */
const BUILDING_SOURCE_CANDIDATES = ["openmaptiles", "carto", "openfreemap", "protomaps"];

const TERRAIN_SOURCE_ID = "meshatlas-terrain";
const BUILDINGS_LAYER_ID = "meshatlas-3d-buildings";
export const DEFAULT_TERRAIN_EXAGGERATION = 1.5;

// MapLibre decays tile resolution toward the horizon on pitched views (see
// createCalculateTileZoomFunction in maplibre-gl/src/geo/projection/covering_tiles.ts).
// Of the two knobs setSourceTileLodParams() exposes, tileCountMaxMinRatio
// only binds once the pitched view's natural tile count already exceeds it
// -- measured empirically against this map's ~37 deg FOV/maxPitch 85, it did
// basically nothing even cranked to 100. maxZoomLevelsOnScreen is the one
// that actually matters here: MapLibre's own default (9.314) aggressively
// decays zoom toward the horizon; *lowering* it (confirmed via network
// tile-request counts and visibly more distant labels/geometry rendering)
// is what keeps distant tiles at a usable resolution instead of dropping
// out once they cross the building layer's minzoom. tileCountMaxMinRatio is
// left fixed at a generous constant since tuning it has no visible effect.
const MAPLIBRE_DEFAULT_MAX_ZOOM_LEVELS_ON_SCREEN = 9.314;
const BUILDING_TILE_COUNT_RATIO = 10;
export const DEFAULT_BUILDING_DRAW_DISTANCE_LEVEL = 3;

// Set once setUpTerrainAndBuildings() first runs and left in place after
// that -- it's a map control (addControl), not a style layer, so it
// survives setStyle() and must NOT be re-created/re-added on the later
// calls setMapTheme() makes on every theme switch (that stacked a new
// terrain-toggle button on the map each time). Kept around so
// setTerrainExaggeration() can update its `options.exaggeration` too --
// TerrainControl's own on/off button (_toggleTerrain in maplibre-gl)
// re-reads that field every time it turns terrain back on, so without this
// a slider change would get silently reverted the next time the user
// toggled terrain off and on. Also doubles as the "current terrain
// exaggeration" source of truth for setUpTerrainAndBuildings() itself, so a
// theme switch re-applies whatever the user last set rather than resetting
// to DEFAULT_TERRAIN_EXAGGERATION.
let terrainControl: TerrainControl | null = null;

// Set once setUpTerrainAndBuildings() resolves a building source, so
// setBuildingRenderDistance() can re-target the same source later. Note
// setSourceTileLodParams() applies to every layer sharing this vector
// source (roads, land use, labels, ...), not just buildings -- MapLibre's
// tile LOD is a per-source property, there's no per-layer equivalent.
let buildingSourceId: string | null = null;

// Current "Building draw distance" slider level, so a theme switch's
// re-added buildings layer (see setUpTerrainAndBuildings()) re-applies it
// instead of resetting to DEFAULT_BUILDING_DRAW_DISTANCE_LEVEL.
let currentBuildingDrawDistanceLevel = DEFAULT_BUILDING_DRAW_DISTANCE_LEVEL;

export function createMap(container: HTMLElement, initialTheme: Theme): MapLibreMap {
  const savedViewport = loadSavedViewport();

  const map = new MapLibreMap({
    container,
    style: styleUrlFor(initialTheme),
    center: savedViewport ? [savedViewport.lng, savedViewport.lat] : [0, 20],
    zoom: savedViewport?.zoom ?? 2,
    pitch: savedViewport?.pitch ?? 45,
    bearing: savedViewport?.bearing ?? 0,
    maxPitch: 85,
    canvasContextAttributes: { antialias: true },
  });

  map.addControl(new NavigationControl({ visualizePitch: true }), "top-left");
  map.addControl(new ScaleControl({ unit: "metric" }), "bottom-left");
  map.addControl(new GeolocateControl({ trackUserLocation: false }), "top-left");

  map.on("load", () => setUpTerrainAndBuildings(map, initialTheme));
  persistViewportOnChange(map);

  return map;
}

/** Switches the basemap to `theme`'s style. setStyle() diffs against
 * everything currently in the style -- including our own programmatically
 * added terrain/hillshade/buildings sources and layers (see
 * setUpTerrainAndBuildings) -- so all of that gets torn down and must be
 * re-added once the new style finishes loading, same as the initial "load"
 * handler in createMap() does. Controls (added via addControl, e.g.
 * TerrainControl and the deck.gl links overlay) aren't style-scoped and
 * survive untouched, so they're not re-added here. `onReady` lets callers
 * (main.ts) re-add their own style-scoped additions -- the history trail
 * layer and NodesLayer's source/layers -- once ours are back in place. */
export function setMapTheme(map: MapLibreMap, theme: Theme, onReady?: () => void): void {
  map.once("style.load", () => {
    setUpTerrainAndBuildings(map, theme);
    onReady?.();
  });
  map.setStyle(styleUrlFor(theme));
}

function setUpTerrainAndBuildings(map: MapLibreMap, theme: Theme): void {
  // On a theme switch, terrainControl already exists and holds whatever the
  // user last set via the exaggeration slider -- reuse it rather than
  // resetting to the default (see the terrainControl comment above).
  const exaggeration = terrainControl?.options.exaggeration ?? DEFAULT_TERRAIN_EXAGGERATION;

  map.addSource(TERRAIN_SOURCE_ID, {
    type: "raster-dem",
    tiles: [TERRAIN_DEM_URL],
    tileSize: 256,
    maxzoom: 15,
    encoding: "terrarium",
    attribution: "Terrain: AWS Terrain Tiles (Mapzen/Amazon)",
  });
  map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration });

  map.addLayer({
    id: "meshatlas-hillshade",
    type: "hillshade",
    source: TERRAIN_SOURCE_ID,
    paint: {
      "hillshade-exaggeration": 0.3,
      // MapLibre's default hillshade-shadow-color is pure black, which on
      // top of an already-dark basemap reads as solid black in low-relief
      // or coarse-DEM areas (e.g. at low zoom). A dark blue-gray keeps the
      // shading legible without crushing everything to black.
      "hillshade-shadow-color": "#1a2230",
      "hillshade-highlight-color": "#8a97a8",
    },
  });

  // TerrainControl is a map control (added via addControl), not a style
  // layer -- it survives setStyle() untouched, unlike everything else in
  // this function. Re-creating and re-adding it on every theme switch (this
  // function also runs from setMapTheme()'s "style.load" handler) stacked a
  // new terrain-toggle button on the map on every toggle instead of reusing
  // the one already there.
  if (!terrainControl) {
    terrainControl = new TerrainControl({
      source: TERRAIN_SOURCE_ID,
      exaggeration: DEFAULT_TERRAIN_EXAGGERATION,
    });
    map.addControl(terrainControl, "top-left");
  }

  // Dark-style-specific -- Positron doesn't have OpenFreeMap dark's low-zoom
  // water/background contrast problem, and this patch's fixed dark-blue
  // fill would look wrong painted over a light basemap.
  if (theme === "night") improveLowZoomContrast(map);

  buildingSourceId = pickBuildingSource(map);
  if (buildingSourceId) {
    map.addLayer({
      id: BUILDINGS_LAYER_ID,
      type: "fill-extrusion",
      source: buildingSourceId,
      "source-layer": "building",
      minzoom: 12,
      paint: {
        "fill-extrusion-color": "#5c6b7a",
        "fill-extrusion-height": buildingHeightExpr(exaggeration),
        "fill-extrusion-base": buildingBaseExpr(exaggeration),
        "fill-extrusion-opacity": 0.85,
      },
    });
    setBuildingRenderDistance(map, currentBuildingDrawDistanceLevel);
  }
}

/** Called from the "Building draw distance" slider (0 = MapLibre's stock
 * horizon falloff, higher = more/further distant tile detail -- see the
 * constant comment above for why this drives maxZoomLevelsOnScreen down
 * rather than tileCountMaxMinRatio up). No-op before the building source
 * has resolved (map "load" hasn't fired yet). */
export function setBuildingRenderDistance(map: MapLibreMap, level: number): void {
  currentBuildingDrawDistanceLevel = level;
  if (!buildingSourceId) return;
  const maxZoomLevelsOnScreen = MAPLIBRE_DEFAULT_MAX_ZOOM_LEVELS_ON_SCREEN - level;
  map.setSourceTileLodParams(maxZoomLevelsOnScreen, BUILDING_TILE_COUNT_RATIO, buildingSourceId);
}

/** Scaling building height/base by the same factor as the terrain slider
 * keeps buildings visually proportionate to the (exaggerated) terrain
 * relief around them instead of looking flat/undersized once the ground
 * is stretched vertically. */
function buildingHeightExpr(exaggeration: number): DataDrivenPropertyValueSpecification<number> {
  return ["*", exaggeration, ["coalesce", ["get", "render_height"], ["get", "height"], 6]];
}

function buildingBaseExpr(exaggeration: number): DataDrivenPropertyValueSpecification<number> {
  return ["*", exaggeration, ["coalesce", ["get", "render_min_height"], ["get", "min_height"], 0]];
}

/** OpenFreeMap's "dark" style keeps water only a couple of shades lighter
 * than the background (rgb(27,27,29) vs rgb(12,12,12)), which reads as a
 * plain black rectangle at world zoom -- exactly the view a user sees
 * before any node data has loaded (or fitToNodesOnce() has run). Nudging
 * just the water fill to a clearly distinct dark blue restores visible
 * coastlines/continents without fighting the rest of the style's dark
 * palette. Guarded with getLayer() checks since a custom MAP_STYLE_URL
 * may not use the OpenMapTiles "water"/"water_intermittent" layer ids at
 * all -- in that case this is a harmless no-op. */
function improveLowZoomContrast(map: MapLibreMap): void {
  for (const layerId of ["water", "water_intermittent"]) {
    if (map.getLayer(layerId)) {
      map.setPaintProperty(layerId, "fill-color", "#1b2838");
    }
  }
}

/** Called from the terrain-exaggeration slider on every "input" tick.
 * Updates the live terrain (a no-op if terrain is currently toggled off via
 * TerrainControl) and the control's own options so a subsequent toggle-off/on
 * doesn't revert to DEFAULT_TERRAIN_EXAGGERATION -- see the terrainControl
 * comment above. Cheap: raster-dem elevation is sampled per-frame in the
 * vertex shader, not baked into a tile bucket, so this redraws instantly. */
export function setTerrainExaggeration(map: MapLibreMap, exaggeration: number): void {
  if (terrainControl) terrainControl.options.exaggeration = exaggeration;
  if (map.getTerrain()) map.setTerrain({ source: TERRAIN_SOURCE_ID, exaggeration });
}

/** Called from the terrain-exaggeration slider's "change" event (drag
 * release / arrow-key commit) rather than "input" -- unlike terrain,
 * fill-extrusion-height/base are data-driven paint properties, so MapLibre
 * has to re-parse and rebuild every already-loaded building tile's vertex
 * buffers in the worker on each call (see Style#_reloadSource). Calling this
 * on every "input" tick queued a reload per intermediate value during a
 * single drag; with enough visible building tiles those overlapping reloads
 * piled up and the visible result silently stayed on a stale value until
 * some unrelated camera move (which forces a full source update) flushed it.
 * Firing once on release avoids the pile-up entirely. */
export function setBuildingExaggeration(map: MapLibreMap, exaggeration: number): void {
  if (!map.getLayer(BUILDINGS_LAYER_ID)) return;
  map.setPaintProperty(BUILDINGS_LAYER_ID, "fill-extrusion-height", buildingHeightExpr(exaggeration));
  map.setPaintProperty(BUILDINGS_LAYER_ID, "fill-extrusion-base", buildingBaseExpr(exaggeration));
}

function pickBuildingSource(map: MapLibreMap): string | null {
  const sources = map.getStyle().sources ?? {};
  for (const candidate of BUILDING_SOURCE_CANDIDATES) {
    if (sources[candidate]?.type === "vector") return candidate;
  }
  const firstVectorSource = Object.entries(sources).find(([, src]) => src.type === "vector");
  return firstVectorSource ? firstVectorSource[0] : null;
}
